import { useState, useEffect } from 'react'

import { Menu, Button } from 'antd'
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import styled, { createGlobalStyle } from 'styled-components'
import { useNavigate, useLocation } from 'react-router-dom'
import type { MenuProps } from 'antd'
import { useSelector } from 'react-redux'

interface MenuItemConfig {
  key: string
  label: string
  icon?: React.ReactNode
  path?: string
  children?: MenuItemConfig[]
}

type MenuItem = Required<MenuProps>['items'][number]

export const MenuList = () => {
  const menuInfo = useSelector((state: any) => state.menu.currentMenu)
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [menuItemConfig, setMenuItemConfig] = useState<MenuItemConfig[]>([])
  const [selectedKeys, setSelectedKeys] = useState([location.pathname])

  // 处理子菜单项
  const processSubMenuItems = (menuData: any): MenuItemConfig[] => {
    if (!menuData?.props?.children) return []

    const children = menuData.props.children
    const processChild = (child: any, index: number): MenuItemConfig => {
      const hasSubMenu = child.props.subMenuItems
      const processedChildren = hasSubMenu
        ? processSubMenuItems(child.props.subMenuItems)
        : undefined

      // 生成唯一 key
      const uniqueKey = child.props.to || `${child.props.text}-${index}`

      // 如果有子菜单，不设置 path（SubMenu 不需要 path）
      if (hasSubMenu && processedChildren && processedChildren.length > 0) {
        return {
          label: child.props.text,
          key: uniqueKey,
          icon: React.isValidElement(child.props.icon) ? child.props.icon : null,
          children: processedChildren,
        }
      }

      // 普通菜单项（没有子菜单）
      return {
        label: child.props.text,
        key: uniqueKey,
        path: child.props.to || '',
        icon: React.isValidElement(child.props.icon)
          ? child.props.level === 1
            ? child.props.icon
            : null
          : null,
      }
    }

    if (Array.isArray(children)) {
      return children
        .filter(child => child?.props && child.props.show)
        .map((child, index) => processChild(child, index))
    }

    return children?.props && children.props.show ? [processChild(children, 0)] : []
  }

  const convertToAntdMenuItems = (config: MenuItemConfig[]): MenuItem[] =>
    config.map(item => {
      // 如果有子菜单，返回 SubMenu 配置
      if (item.children && item.children.length > 0) {
        return {
          key: item.key,
          label: item.label,
          icon: item.icon,
          children: convertToAntdMenuItems(item.children),
          type: 'submenu' as any,
        } as any
      }

      // 普通菜单项
      return {
        key: item.key,
        label: item.label,
        icon: item.icon,
      }
    })

  const items = convertToAntdMenuItems(menuItemConfig)

  // 进入页面自动打开选中的菜单栏
  const removeAfterLastSlash = () => {
    const count = location.pathname.replace('/detail', '').split('/')
    const lastIndex = location.pathname.lastIndexOf('/')
    if (count.length - 1 === 4) {
      return [count.slice(0, 3).join('/'), count.slice(0, 4).join('/')]
    }
    if (count.length - 1 === 3) {
      return [count.slice(0, 2).join('/'), count.slice(0, 3).join('/')]
    }
    if (lastIndex !== -1) {
      const result = location.pathname.substring(0, lastIndex)
      return [result]
    }
    // 如果字符串中没有斜杠，则返回原始字符串
    return [location.pathname]
  }

  const [openKeys, setOpenKeys] = useState<string[]>(removeAfterLastSlash())

  //给子账号菜单栏加一层限制，等待接口调用后再渲染菜单栏，避免菜单栏风险闪现的问题
  // const [menuShow, setMenuShow] = useState(true)

  useEffect(() => {
    if (menuInfo.text) {
      const menuItems = processSubMenuItems(menuInfo.subMenuItems)
      setMenuItemConfig(menuItems)

      // 获取当前路由的完整路径
      const currentPath = pathname
      // 找到对应的菜单项
      const findMenuByPath = (
        menuConfigs: MenuItemConfig[],
        path: string,
      ): MenuItemConfig | null => {
        for (const item of menuConfigs) {
          if (item.path === path) return item
          if (item.children) {
            const found = findMenuByPath(item.children, path)
            if (found) return found
          }
        }
        return null
      }

      const targetMenu = findMenuByPath(menuItems, currentPath)
      if (targetMenu) {
        setSelectedKeys([targetMenu.path || ''])
        // 如果是子菜单，展开其父菜单
        const parentPath = targetMenu.path?.split('/').slice(0, -1).join('/')
        if (parentPath) {
          setOpenKeys(prevKeys => {
            const newKeys = [...prevKeys]
            if (!newKeys.includes(parentPath)) {
              newKeys.push(parentPath)
            }
            return newKeys
          })
        }
      }

      if (
        (menuInfo.menuName === 'validation' && /^\/validation\/?$/.test(pathname)) ||
        (menuInfo.menuName === 'report' && /^\/report\/?$/.test(pathname))
      ) {
        const firstMenuItem = menuItems[0]
        const firstChild = firstMenuItem?.children?.[0]
        if (firstChild?.path) {
          navigate(firstChild.path)
        }
      }
    } else {
      // 如果没有菜单信息，尝试从当前路径恢复菜单状态
      const pathSegments = pathname.split('/').filter(Boolean)
      if (pathSegments.length > 0) {
        const rootPath = `/${pathSegments[0]}`
        const parentPath =
          pathSegments.length > 1 ? `/${pathSegments.slice(0, -1).join('/')}` : rootPath

        setOpenKeys([parentPath])
        setSelectedKeys([pathname])
      }
    }
  }, [menuInfo, pathname])

  // 添加一个新的 useEffect 来处理 URL 直接访问的情况
  useEffect(() => {
    const pathSegments = pathname.split('/').filter(Boolean)
    if (pathSegments.length > 1) {
      const parentPath = `/${pathSegments.slice(0, -1).join('/')}`
      setOpenKeys(prevKeys => {
        const newKeys = [...prevKeys]
        if (!newKeys.includes(parentPath)) {
          newKeys.push(parentPath)
        }
        return newKeys
      })
    }
  }, [pathname])

  useEffect(() => {
    // 当路由变化时，更新选中的菜单项
    setOpenKeys(val => val)
    if (!collapsed) {
      setOpenKeys(removeAfterLastSlash())
    }
    setSelectedKeys(() => {
      if (
        location.pathname.indexOf('/detail') != -1 ||
        location.pathname.indexOf('/catalog') != -1 ||
        location.pathname.indexOf('/siteTree') != -1
      ) {
        return [
          location.pathname
            ?.replace('/detail', '')
            ?.replace('/catalog', '')
            ?.replace('/siteTree', ''),
        ]
      }
      return [location.pathname]
    })
  }, [location])

  //只能展示一个父级
  const onOpenChange = (keys: string[]) => {
    // 折叠状态下不处理 openKeys，让 SubMenu 自己处理 popup
    if (collapsed) {
      return
    }

    const latestOpenKey = keys.find(key => !openKeys.includes(key))
    if (latestOpenKey) {
      setOpenKeys([latestOpenKey])
    } else {
      setOpenKeys([])
    }
  }

  //路由跳转
  const onSelect = e => {
    // 只有当 key 是路径格式时才进行路由跳转
    if (e.key && e.key.startsWith('/')) {
      navigate(e.key)
      setSelectedKeys([e.key])
      // 获取当前选中菜单的父级路径
      const parentPath = e.key.split('/').slice(0, -1).join('/')
      if (parentPath) {
        // 保持现有的展开菜单，并添加新的父级路径
        setOpenKeys(prevKeys => {
          const newKeys = [...prevKeys]
          if (!newKeys.includes(parentPath)) {
            newKeys.push(parentPath)
          }
          return newKeys
        })
      }
    }
  }

  // 切换展开收起
  const toggleCollapsed = () => {
    setCollapsed(!collapsed)
  }

  return (
    <>
      <GlobalMenuStyle />
      <MenuleftContainer collapsed={collapsed}>
        <div className="collapse-box">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleCollapsed}
            className="btn"
            style={{
              color: '#fff',
              fontSize: '16px',
            }}
          />
        </div>
        <Menu
          style={{ background: '#020E46', height: 'calc(100% - 42px)', overflowY: 'auto' }}
          mode="inline"
          defaultOpenKeys={collapsed ? [] : removeAfterLastSlash()}
          openKeys={collapsed ? undefined : openKeys}
          onSelect={onSelect}
          defaultSelectedKeys={selectedKeys}
          selectedKeys={selectedKeys}
          onOpenChange={collapsed ? undefined : onOpenChange}
          theme="dark"
          items={items}
          inlineCollapsed={collapsed}
          subMenuOpenDelay={0.1}
          subMenuCloseDelay={0.1}
          triggerSubMenuAction="hover"
          getPopupContainer={() => document.body}
        />
      </MenuleftContainer>
    </>
  )
}

const GlobalMenuStyle = createGlobalStyle`
  /* 折叠菜单弹出层全局样式 */
  .ant-menu-submenu-popup.ant-menu-dark,
  .ant-menu-submenu-popup {
    background: linear-gradient(180deg, #0f065a 0%, #080039 100%) !important;
    border-radius: 8px !important;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.3) !important;
    z-index: 1050 !important;
    min-width: 200px !important;
    
    .ant-menu {
      background: transparent !important;
      border: none !important;
    }
    
    .ant-menu-sub {
      background: transparent !important;
    }
    
    .ant-menu-item {
      height: 48px !important;
      line-height: 48px !important;
      color: #fff !important;
      margin: 0 !important;
      padding: 0 16px !important;
      
      &:hover {
        background: #4c4f72 !important;
      }
    }
    
    .ant-menu-item-selected {
      background: linear-gradient(90deg, #342bb8 0%, rgba(32, 144, 168, 0.8) 100%) !important;
      position: relative;
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 4px;
        height: 100%;
        background: linear-gradient(180deg, #9f98ff 0%, #30a1b9 100%);
      }
    }
    
    .ant-menu-submenu-title {
      height: 48px !important;
      line-height: 48px !important;
      color: #fff !important;
      
      &:hover {
        background: #4c4f72 !important;
      }
    }
  }
  
  /* 确保折叠状态下的 SubMenu 可以交互 */
  .ant-menu-inline-collapsed {
    .ant-menu-submenu {
      position: relative !important;
      
      .ant-menu-submenu-title {
        padding: 0 calc(50% - 16px / 2) !important;
        text-align: center;
        cursor: pointer !important;
        pointer-events: auto !important;
      }
      
      &:hover {
        .ant-menu-submenu-title {
          background: rgba(255, 255, 255, 0.1) !important;
        }
      }
    }
  }
`

const MenuleftContainer = styled.div`
  width: ${props => (props.collapsed ? '56px' : '200px')};
  transition: width 0.3s ease;
  height: 100%;
  background: linear-gradient(180deg, #0f065a 0%, #080039 100%);
  position: relative;
  z-index: 100;
  overflow: visible;

  .collapse-box {
    display: flex;
    align-items: center;
    padding: 6px 8px;
    .btn {
      width: 100%;
      height: 30px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 6px;
    }
  }

  .ant-menu.ant-menu-inline {
    background: transparent;
  }

  .ant-menu.ant-menu-vertical {
    background: none;
  }

  .ant-menu-item {
    height: 48px !important;
    line-height: 48px !important;
  }

  .ant-menu-dark.ant-menu-dark:not(.ant-menu-horizontal) .ant-menu-item-selected {
    background: linear-gradient(90deg, #342bb8 0%, rgba(32, 144, 168, 0.8) 100%);
    position: relative;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 4px;
      height: 100%;
      background: linear-gradient(180deg, #9f98ff 0%, #30a1b9 100%);
    }
  }

  .ant-menu.ant-menu-inline-collapsed {
    width: 56px;

    > .ant-menu-item,
    > .ant-menu-submenu > .ant-menu-submenu-title {
      padding: 0 calc(50% - 16px / 2) !important;
      text-align: center;
    }
  }
`
